import axios from "axios";
import { withNamespaces } from "react-i18next"
import { toast } from "react-toastify";
import {
    Container,
    Row,
    Col,
    Button,
    Card,
    CardBody,
    Alert,
    Spinner
} from "reactstrap";
import Navigator from "src/modules/data-protection/components/navigator";
import { useMemo, useRef, useState } from "react";
import { API_BASE_URL } from "src/modules/data-protection/constants";
import debounce from "lodash.debounce";
import { connect } from "react-redux";
import UserUtils from "src/services/utils/UserUtils";

const DeviceAgent = ({ t, user, token }) => {
    const userUtils = new UserUtils();

    const [ downloadPercent, setDownloadPercent ] = useState(-1);

    const updatePercent = useRef(
        debounce((percent) => {
            setDownloadPercent(() => percent);
        }, 100)
    ).current;

    const downloadFile = () => {
        setDownloadPercent(0);

        axios.get(`${API_BASE_URL}/client-download`, {
            responseType: "arraybuffer",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            onDownloadProgress: (e) => {
                updatePercent(Math.round((e.loaded * 100) / e.total));
            },
        })
        .then((blob) => {
            const link = document.createElement("a");
        
            link.href = window.URL.createObjectURL(new Blob([blob.data]));
            link.setAttribute("download", 'SIP-Integration-Azure-AD-App.exe');
        
            document.body.appendChild(link);
        
            link.click();
            link.remove();
        })
        .catch(() => {
            toast(t("An error occurred while downloading the file."), {
                type: "error"
            });
        })
        .finally(() => {
            setTimeout(() => {
                setDownloadPercent(-1);
            }, 500);
        });
    };

    const navigatorOptions = useMemo(() => {
        return {
            backButtonOptions: (backButtonOptions) => {
                return {
                    ...backButtonOptions,
                    enable: false
                }
            },
            breadCrumbs: [
                {
                    title: 'SIP',
                    link: userUtils.isAnalystOrAnalystAdmin(user) ? '/admin/data-protection/dashboard' : '/employee/home?landing=false'
                },
                {
                    title: 'Integration',
                    link: '#'
                }
            ],
            pageTitle: `${t('Integration')}: ${t('Software Sync')}`
        }
    }, [
        user,
        t
    ]);

    return (
        <div className="page-content">
            <Container fluid>
                <Row className="mb-4">
                    <Col lg="12">
                        <Navigator {...navigatorOptions} />
                    </Col>
                </Row>
                
                <Row className="mb-4">
                    <Col>
                        <Alert 
                            color="info"
                            className="d-flex justify-content-start"
                        >
                            <div className="d-flex align-items-center me-2">
                                <i className="ri-information-line fs-1" />
                            </div>
                            <div className="d-flex align-items-start justify-content-center flex-column">
                                {t('This portable agent helps your organization maintain an up-to-date software inventory by collecting installed software details from Windows 10/11 devices joined to Azure Active Directory. It runs once and securely transmits the data to your GDPR platform using encryption. No installation or background service is required.')}
                            </div>
                        </Alert>
                    </Col>
                </Row>

                <Card className="mb-5 shadow-sm">
                    <CardBody>
                        <h5>
                            <i className="ri-download-2-line me-2 fs-3 align-middle" />
                            {t('Step 1 – Download the Agent')}
                        </h5>
                        <p>
                            {t('Click the button below to download the portable app for Windows 10/11.')}
                            {t('This tool only works on devices joined to Azure Active Directory (AAD).')}
                        </p>
                        <Button
                            color="primary"
                            rel="noopener noreferrer"
                            onClick={downloadFile}
                            disabled={downloadPercent > -1}
                        >
                            {downloadPercent > -1 ? (
                                <>
                                    <Spinner
                                        size="sm"
                                        color="white"
                                        className="me-1"
                                    />
                                    {t('Downloading')}... ({downloadPercent}%)
                                </>
                            ) : (
                                <>
                                    <i className="ri-download-cloud-line me-1 fs-4 align-middle" />
                                    {t('Download for Windows')} (.exe)
                                </>
                            )}
                        </Button>
                    </CardBody>
                </Card>

                <Card className="mb-4 shadow-sm">
                    <CardBody>
                        <h5>
                            <i className="ri-tools-line me-2 fs-3 align-middle" />
                            {t('Step 2 – Run the Agent')}
                        </h5>
                        <ul>
                            <li>{t('Run the downloaded .exe file directly — no installation needed.')}</li>
                            <li>{t('Ensure the device is connected to the internet and joined to Azure AD.')}</li>
                            <li>{t('The app will execute once, collect software data, and exit automatically.')}</li>
                        </ul>
                    </CardBody>
                </Card>

                <Card className="mb-4 shadow-sm">
                    <CardBody>
                        <h5>
                            <i className="ri-slideshow-line me-2 fs-3 align-middle" />
                            {t('What the Agent Does')}
                        </h5>
                        <ul>
                            <li>{t('Verifies Azure Active Directory join status')}</li>
                            <li>{t('Collects a list of installed software from the Windows registry')}</li>
                            <li>{t('Associates the inventory with the current user’s Azure identity')}</li>
                            <li>{t('Sends the data securely to your GDPR platform backend')}</li>
                        </ul>
                    </CardBody>
                </Card>

                {userUtils.isAnalystOrAnalystAdmin() && (
                    <Card className="mb-4 shadow-sm">
                        <CardBody>
                            <h5>
                                <i className="ri-download-2-line me-2 fs-3 align-middle" />
                                {t('Step 3 – Load Collected Software')}
                            </h5>
                            <ul>
                                <li>{t('Go to Asset Register → Software in your GDPR platform.')}</li>
                                <li>{t('At the top-right of the software list, click the Actions dropdown menu.')}</li>
                                <li>{t('Select Fetch Software to load the collected software data into your inventory.')}</li>
                            </ul>
                        </CardBody>
                    </Card>
                )}

                <Card className="mb-4 shadow-sm">
                    <CardBody>
                        <h5>
                            <i className="ri-lock-2-line me-2 fs-3 align-middle" />
                            {t('Security & Privacy')}
                        </h5>
                        <ul>
                            <li>{t('No personal files or activity data are accessed')}</li>
                            <li>{t('The app does not install or run in the background')}</li>
                            <li>{t("No uninstallation is required — it's portable and disposable")}</li>
                        </ul>

                        <h6 className="mt-4">
                            <i className="ri-shield-keyhole-line me-2 fs-3 align-middle" />
                            {t('Encryption')}
                        </h6>
                        <ul className="mb-0">
                            <li>{t('Uses ECDH key exchange to generate a secure session')}</li>
                            <li>{t('Client and server exchange public keys over HTTPS')}</li>
                            <li>{t('Data (e.g. installed software list, username, device ID) is encrypted using the derived shared key')}</li>
                        </ul>
                    </CardBody>
                </Card>
            </Container>
        </div>
    )
}

const mapStateToProps = (state) => {
    const { token, user } = state.Login;

	return {
		token,
        user
	};
}

export default withNamespaces()(
    connect(mapStateToProps)(
        DeviceAgent
    )   
)